import axios from 'axios';
import { ApiResponse, ApiError } from '../types';
import { getCurrentHotelCode } from '../utils/hotelContext';
import { 
  isTokenExpired, 
  isTokenExpiringSoon, 
  getTokenInfo, 
  storeToken, 
  loadToken, 
  clearToken,
  getBookingReferenceFromToken
} from '../utils/tokenUtils';

class GuestApiClient {
  private client: any;
  private token: string | null = null;
  private hotelContext: string | null = null;
  private hotelCode: string | null = null;
  private reservationId: string | null = null;
  private refreshPromise: Promise<string> | null = null;
  private requestQueue: Array<{ resolve: Function; reject: Function; config: any }> = [];
  private isRefreshing: boolean = false;

  constructor() {
    console.log('Environment REACT_APP_API_URL:', process.env.REACT_APP_API_URL);

    const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000';
    
    this.client = axios.create({
      baseURL: apiUrl,
      timeout: 30000,
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
    });

    console.log('API Client initialized:', {
      baseURL: apiUrl,
      envValue: process.env.REACT_APP_API_URL,
      apiKey: process.env.REACT_APP_API_KEY ? 'configured' : 'not configured'
    });

    this.setupInterceptors();
  }

  private setupInterceptors() {
    // Request interceptor
    this.client.interceptors.request.use(
      (config: any) => {
        if (this.token) {
          config.headers.Authorization = `Bearer ${this.token}`;
        }
        // Hotel context is now extracted from JWT token by the backend middleware
        // No need to send x-hotel-context header anymore to avoid CORS issues
        return config;
      },
      (error: any) => Promise.reject(error)
    );

    // Response interceptor with token refresh
    this.client.interceptors.response.use(
      (response: any) => response,
      async (error: any) => {
        const originalRequest = error.config;

        // A 401 from a LOGIN/verification call means "those details are
        // wrong", not "your session expired" — refreshing and bouncing
        // to the home screen there would throw the guest out of the form
        // they are still filling in. Let the caller show the error.
        const url = originalRequest?.url || '';
        const isAuthAttempt =
          url.includes('/auth/login') ||
          url.includes('/auth/secure-login') ||
          url.includes('/checkout/verify') ||
          url.includes('/verify-otp') ||
          url.includes('/send-otp');

        if (error.response?.status === 401 && !isAuthAttempt && !originalRequest._retry) {
          console.warn('Received 401 Unauthorized, attempting token refresh');
          
          // Mark this request as retried to prevent infinite loops
          originalRequest._retry = true;

          try {
            // Try to refresh the token
            const newToken = await this.refreshToken();
            if (newToken) {
              // Update the authorization header and retry the request
              originalRequest.headers.Authorization = `Bearer ${newToken}`;
              return this.client(originalRequest);
            }
          } catch (refreshError) {
            console.error('Token refresh failed:', refreshError);
          }

          // If refresh failed, clear auth and redirect
          console.warn('Token refresh failed, clearing authentication');
          this.clearAuth();
          localStorage.removeItem('guest_auth_state');
          
          // Only redirect if not already on a login/entry page
          if (!window.location.pathname.includes('/booking/') &&
              !window.location.pathname.includes('/hotel/') &&
              !window.location.pathname.includes('/hotel-info') &&
              !window.location.pathname.includes('/login') &&
              !window.location.pathname.includes('/secure/') &&
              window.location.pathname !== '/') {
            window.location.href = '/';
          }
        }
        
        return Promise.reject(error);
      }
    );
  }

  private initializeHotelCode() {
    const hotelCode = getCurrentHotelCode();
    if (hotelCode) {
      this.hotelCode = hotelCode;
      console.log('Hotel code initialized in API client:', hotelCode);
    }
  }

  setHotelCode(hotelCode: string) {
    this.hotelCode = hotelCode;
    console.log('Hotel code set in API client:', hotelCode);
  }

  getHotelCode(): string | null {
    return this.hotelCode;
  }

  setAuth(token: string, hotelId?: string, reservationId?: string) {
    this.token = token;
    // Hotel context is now handled by backend middleware from JWT token
    if (reservationId) {
      this.reservationId = reservationId;
    }
    
    // Use enhanced token storage
    storeToken(token, 'guest_token');
    
    if (reservationId) {
      localStorage.setItem('guest_reservation_id', reservationId);
    }

    // Schedule token refresh
    this.scheduleTokenRefresh();
  }

  clearAuth() {
    this.token = null;
    this.hotelContext = null;
    this.reservationId = null;
    this.isRefreshing = false;
    this.refreshPromise = null;
    
    // Clear any pending requests
    this.requestQueue.forEach(({ reject }) => {
      reject(new Error('Authentication cleared'));
    });
    this.requestQueue = [];
    
    // Note: We don't clear hotelCode here as it should persist across auth sessions
    clearToken('guest_token');
    localStorage.removeItem('guest_reservation_id');
  }

  loadStoredAuth() {
    const storedToken = loadToken('guest_token');
    
    if (storedToken) {
      this.token = storedToken;
      
      // Extract booking reference directly from the JWT token
      const bookingReference = getBookingReferenceFromToken(storedToken);
      if (bookingReference) {
        this.reservationId = bookingReference;
        // Also update localStorage for compatibility
        localStorage.setItem('guest_reservation_id', bookingReference);
      }
      
      // Schedule token refresh for stored token
      this.scheduleTokenRefresh();
    }
  }

  async get<T>(url: string): Promise<ApiResponse<T>> {
    return this.makeRequest('get', url);
  }

  async post<T>(url: string, data?: any): Promise<ApiResponse<T>> {
    return this.makeRequest('post', url, data);
  }

  async put<T>(url: string, data?: any): Promise<ApiResponse<T>> {
    return this.makeRequest('put', url, data);
  }

  async patch<T>(url: string, data?: any): Promise<ApiResponse<T>> {
    return this.makeRequest('patch', url, data);
  }

  async delete<T>(url: string): Promise<ApiResponse<T>> {
    return this.makeRequest('delete', url);
  }

  /**
   * Centralized request method with token refresh handling
   */
  private async makeRequest<T>(method: string, url: string, data?: any): Promise<ApiResponse<T>> {
    // Check if token needs refresh before making request
    if (this.token && isTokenExpiringSoon(this.token, 2)) {
      console.log('Token expiring soon, refreshing before request');
      try {
        await this.refreshToken();
      } catch (error) {
        console.warn('Proactive token refresh failed, continuing with request');
      }
    }

    try {
      let response;
      switch (method.toLowerCase()) {
        case 'get':
          response = await this.client.get(url);
          break;
        case 'post':
          response = await this.client.post(url, data);
          break;
        case 'put':
          response = await this.client.put(url, data);
          break;
        case 'patch':
          response = await this.client.patch(url, data);
          break;
        case 'delete':
          response = await this.client.delete(url);
          break;
        default:
          throw new Error(`Unsupported HTTP method: ${method}`);
      }
      
      return {
        success: true,
        data: response.data,
      };
    } catch (error: any) {
      throw this.handleError(error);
    }
  }

  async uploadFile<T>(url: string, file: File, additionalData?: Record<string, any>, fileFieldName: string = 'file'): Promise<ApiResponse<T>> {
    try {
      const formData = new FormData();
      formData.append(fileFieldName, file);
      
      if (additionalData) {
        Object.keys(additionalData).forEach(key => {
          formData.append(key, additionalData[key]);
        });
      }

      // Log FormData contents for signature uploads
      if (file.name.includes('-signature-') || additionalData?.document_type === 'signature') {
        console.log('=== FORMDATA DEBUG FOR SIGNATURE UPLOAD ===');
        console.log('URL:', url);
        console.log('File field name:', fileFieldName);
        console.log('File details:', {
          name: file.name,
          type: file.type,
          size: file.size,
          lastModified: file.lastModified
        });
        console.log('Additional data sent:', additionalData);
        
        // Log FormData entries (using alternative approach for compatibility)
        const formDataEntries: any = {};
        formDataEntries[fileFieldName] = `[File: ${file.name}, ${file.size} bytes, ${file.type}]`;
        if (additionalData) {
          Object.keys(additionalData).forEach(key => {
            formDataEntries[key] = additionalData[key];
          });
        }
        console.log('Complete FormData entries:', formDataEntries);
        console.log('=== END FORMDATA DEBUG ===');
      }

      const response = await this.client.post(url, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      // Log API response for signature uploads
      if (file.name.includes('-signature-') || additionalData?.document_type === 'signature') {
        console.log('=== API RESPONSE DEBUG FOR SIGNATURE ===');
        console.log('Response status:', response.status);
        console.log('Response data:', response.data);
        console.log('=== END API RESPONSE DEBUG ===');
      }

      return {
        success: true,
        data: response.data,
      };
    } catch (error: any) {
      console.log('=== BATCH UPLOAD ERROR DEBUG ===');
      console.log('Error details:', error);
      console.log('Error response status:', error.response?.status);
      console.log('Error response data:', error.response?.data);
      console.log('Error response headers:', error.response?.headers);
      console.log('Request URL:', error.config?.url);
      console.log('Request method:', error.config?.method);
      console.log('Request headers:', error.config?.headers);
      console.log('=== END BATCH UPLOAD ERROR DEBUG ===');
      throw this.handleError(error);
    }
  }

  /**
   * Upload multiple files with batch processing
   */
  async uploadMultipleFiles<T>(
    url: string, 
    uploads: Array<{
      file: File;
      additionalData?: Record<string, any>;
      fileFieldName?: string;
    }>,
    onProgress?: (completed: number, total: number, currentFile?: string) => void
  ): Promise<ApiResponse<T[]>> {
    try {
      const results: T[] = [];
      const total = uploads.length;

      for (let i = 0; i < uploads.length; i++) {
        const upload = uploads[i];
        
        // Report progress
        if (onProgress) {
          onProgress(i, total, upload.file.name);
        }

        try {
          const result = await this.uploadFile<T>(
            url, 
            upload.file, 
            upload.additionalData, 
            upload.fileFieldName
          );
          results.push(result.data);
        } catch (uploadError) {
          console.error(`Failed to upload ${upload.file.name}:`, uploadError);
          // Continue with other uploads
          results.push({
            success: false,
            error: `Failed to upload ${upload.file.name}`,
            file_name: upload.file.name
          } as any);
        }
      }

      // Final progress update
      if (onProgress) {
        onProgress(total, total);
      }

      return {
        success: true,
        data: results,
      };
    } catch (error: any) {
      throw this.handleError(error);
    }
  }

  /**
   * Upload multiple documents for multiple guests in a single batch request
   */
  async uploadMultipleGuestDocuments<T>(
    uploads: Array<{
      file: File;
      document_type: string;
      document_number: string;
      guest_name?: string;
      guest_relation?: string;
    }>,
    onProgress?: (completed: number, total: number, currentFile?: string) => void
  ): Promise<ApiResponse<T[]>> {
    try {
      console.log('=== BATCH UPLOAD DEBUG ===');
      console.log('Client instance check:', {
        clientExists: !!this.client,
        clientPost: !!this.client?.post,
        clientType: typeof this.client,
        clientKeys: this.client ? Object.keys(this.client) : 'N/A'
      });
      console.log('Uploads array received:', uploads.map(upload => ({
        fileName: upload.file.name,
        fileSize: upload.file.size,
        fileType: upload.file.type,
        documentType: upload.document_type,
        documentNumber: upload.document_number,
        guestName: upload.guest_name,
        guestRelation: upload.guest_relation
      })));

      // Create a single FormData for all uploads
      const formData = new FormData();
      
      uploads.forEach((upload, index) => {
        // Add file with indexed field name
        formData.append(`document_image_${index}`, upload.file);
        
        // Add metadata for each upload
        formData.append(`document_type_${index}`, upload.document_type);
        formData.append(`document_number_${index}`, upload.document_number);
        if (upload.guest_name) {
          formData.append(`guest_name_${index}`, upload.guest_name);
        }
        if (upload.guest_relation) {
          formData.append(`guest_relation_${index}`, upload.guest_relation);
        }
      });

      // For single file uploads (like signature), also send without index for backend compatibility
      if (uploads.length === 1) {
        const upload = uploads[0];
        formData.append('document_type', upload.document_type);
        formData.append('document_number', upload.document_number);
        if (upload.guest_name) {
          formData.append('guest_name', upload.guest_name);
        }
        if (upload.guest_relation) {
          formData.append('guest_relation', upload.guest_relation);
        }
      }

      console.log('FormData contents:', Array.from(formData.entries()));
      
      // Debug authentication status
      console.log('Authentication debug:', {
        hasToken: !!this.token,
        tokenLength: this.token?.length,
        tokenPrefix: this.token?.substring(0, 20) + '...',
        reservationId: this.reservationId,
        hotelCode: this.hotelCode
      });
      
      console.log('=== END BATCH UPLOAD DEBUG ===');

      // Report initial progress
      if (onProgress) {
        onProgress(0, uploads.length, uploads[0]?.file.name);
      }

      // Additional client check before making the call
      if (!this.client) {
        console.error('Client is null/undefined, reinitializing...');
        // Re-initialize the client if it's undefined
        const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000';
        this.client = axios.create({
          baseURL: apiUrl,
          timeout: 30000,
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
        });
        this.setupInterceptors();
      }
      
      if (!this.client.post) {
        throw new Error('API client post method is not available');
      }

      console.log('Making API call to /api/guest/pre-checkin/upload-multiple-ids/');
      console.log('Request headers will include:', {
        'Content-Type': 'multipart/form-data',
        'Authorization': this.token ? `Bearer ${this.token.substring(0, 20)}...` : 'No token'
      });

      const response = await this.client.post('/api/guest/pre-checkin/upload-multiple-ids/', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      // Report completion
      if (onProgress) {
        onProgress(uploads.length, uploads.length);
      }

      // Log API response for signature uploads
      const hasSignature = uploads.some(upload => 
        upload.document_type === 'signature' || 
        upload.file.name.includes('-signature-')
      );
      
      if (hasSignature) {
        console.log('=== BATCH API RESPONSE DEBUG FOR SIGNATURE ===');
        console.log('Response status:', response.status);
        console.log('Response data:', response.data);
        console.log('Upload results from API:', response.data.data);
        console.log('=== END BATCH API RESPONSE DEBUG ===');
      }

      // Backend returns { success: true, data: [upload_results], batch_status: {...} }
      // We need to return the upload results array
      return {
        success: true,
        data: response.data.data || response.data, // Use data array from batch response
      };
      
    } catch (error: any) {
      console.log('=== BATCH UPLOAD ERROR DEBUG ===');
      console.log('Error details:', error);
      console.log('Error response status:', error.response?.status);
      console.log('Error response data:', error.response?.data);
      console.log('Error response headers:', error.response?.headers);
      console.log('Request URL:', error.config?.url);
      console.log('Request method:', error.config?.method);
      console.log('Request headers:', error.config?.headers);
      console.log('Authentication token present:', !!this.token);
      console.log('=== END BATCH UPLOAD ERROR DEBUG ===');
      
      throw this.handleError(error);
    }
  }

  private handleError(error: any): ApiError {
    console.error('API Error Details:', {
      message: error.message,
      status: error.response?.status,
      statusText: error.response?.statusText,
      data: error.response?.data,
      url: error.config?.url,
      method: error.config?.method,
      headers: error.config?.headers,
    });

    if (error.response) {
      // Server responded with error status
      const errorMessage = error.response.data?.message || 
                          error.response.data?.error || 
                          error.response.data?.detail ||
                          `Server error: ${error.response.status} ${error.response.statusText}`;
      
      return {
        success: false,
        error: errorMessage,
        details: error.response.data?.details || error.response.data?.errors,
        code: error.response.data?.code,
        body: error.response.data,
      };
    } else if (error.request) {
      // Network error
      return {
        success: false,
        error: 'Network error. Please check your internet connection and CORS configuration.',
      };
    } else {
      // Other error
      return {
        success: false,
        error: error.message || 'An unexpected error occurred',
      };
    }
  }

  // Utility method to check if user is authenticated
  isAuthenticated(): boolean {
    return !!this.token;
  }

  // Get current reservation ID
  getReservationId(): string | null {
    return this.reservationId;
  }

  /**
   * Refresh the JWT token
   */
  private async refreshToken(): Promise<string | null> {
    if (this.isRefreshing && this.refreshPromise) {
      // If already refreshing, wait for the existing refresh to complete
      return this.refreshPromise;
    }

    this.isRefreshing = true;
    
    this.refreshPromise = new Promise(async (resolve, reject) => {
      try {
        console.log('Attempting to refresh token...');
        
        // Try to use the existing token to get a new one
        const response = await this.client.post('/api/guest/auth/refresh/', {}, {
          headers: {
            Authorization: `Bearer ${this.token}`
          }
        });

        if (response.data && response.data.access_token) {
          const newToken = response.data.access_token;
          console.log('Token refresh successful');
          
          // Update stored token
          this.token = newToken;
          storeToken(newToken, 'guest_token');
          
          // Process any queued requests
          this.processRequestQueue(newToken);
          
          // Schedule next refresh
          this.scheduleTokenRefresh();
          
          resolve(newToken);
        } else {
          throw new Error('No token in refresh response');
        }
      } catch (error) {
        console.error('Token refresh failed:', error);
        
        // Reject all queued requests
        this.requestQueue.forEach(({ reject }) => {
          reject(error);
        });
        this.requestQueue = [];
        
        reject(error);
      } finally {
        this.isRefreshing = false;
        this.refreshPromise = null;
      }
    });

    return this.refreshPromise;
  }

  /**
   * Schedule automatic token refresh
   */
  private scheduleTokenRefresh(): void {
    if (!this.token) return;

    const tokenInfo = getTokenInfo(this.token);
    if (!tokenInfo) return;

    // Refresh 5 minutes before expiry
    const refreshTime = Math.max(0, tokenInfo.timeUntilExpiry - (5 * 60 * 1000));
    
    if (refreshTime > 0) {
      console.log(`Token refresh scheduled in ${Math.round(refreshTime / 1000 / 60)} minutes`);
      
      setTimeout(() => {
        if (this.token && !isTokenExpired(this.token)) {
          this.refreshToken().catch(error => {
            console.error('Scheduled token refresh failed:', error);
          });
        }
      }, refreshTime);
    } else {
      // Token is already expired or expiring very soon
      console.warn('Token is expired or expiring soon, attempting immediate refresh');
      this.refreshToken().catch(error => {
        console.error('Immediate token refresh failed:', error);
      });
    }
  }

  /**
   * Process queued requests after token refresh
   */
  private processRequestQueue(newToken: string): void {
    this.requestQueue.forEach(({ resolve, config }) => {
      config.headers.Authorization = `Bearer ${newToken}`;
      resolve(this.client(config));
    });
    this.requestQueue = [];
  }

  /**
   * Check if current token is valid
   */
  isTokenValid(): boolean {
    return this.token ? !isTokenExpired(this.token) : false;
  }

  /**
   * Check if token is expiring soon
   */
  isTokenExpiringSoon(): boolean {
    return this.token ? isTokenExpiringSoon(this.token) : true;
  }

  /**
   * Get token expiry information
   */
  getTokenInfo() {
    return this.token ? getTokenInfo(this.token) : null;
  }
}

// Create and export singleton instance
export const guestApiClient = new GuestApiClient();

// Load any stored authentication on initialization
guestApiClient.loadStoredAuth();